/** Open-basket section for a POSITIONAL strategy's detail page. Replaces
 * the intraday OpenPositionSection there — a basket has no SL, peak or
 * trail ratchet, so those fields would only ever render as dashes. Shows
 * the strategy's open basket(s) as BasketTiles, or a quiet placeholder
 * while flat. Fetches LIVE and PAPER like the intraday section (an open
 * basket is an open basket to the operator). */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { OpenPositionSection } from "@/components/OpenPosition/OpenPositionSection";
import { tradeLogApi } from "@/lib/tradeLogApi";
import type { Strategy } from "@/types/strategy";
import type { TradeLogEntry } from "@/types/tradeLog";
import { BasketTile } from "./BasketTile";

const POLL_MS = 15_000;

/** The right open-position surface for a strategy detail page:
 * positional strategies get the basket view (no SL/peak/trail — those
 * would all render as dashes for a basket), intraday ones keep the
 * trail-centric OpenPositionSection. */
export function StrategyPositionSection({
  strategy,
}: Readonly<{ strategy: Pick<Strategy, "id" | "horizon"> }>): ReactElement {
  if (strategy.horizon === "POSITIONAL") {
    return <StrategyBasketSection strategyId={strategy.id} />;
  }
  return <OpenPositionSection strategyId={strategy.id} />;
}

export function StrategyBasketSection({
  strategyId,
}: Readonly<{ strategyId: string }>): ReactElement {
  const q = useQuery({
    queryKey: ["strategy-basket", strategyId] as const,
    queryFn: async () => {
      const base = { range: "today", strategyId, show: "trades" } as const;
      const [live, paper] = await Promise.all([
        tradeLogApi.list({ ...base }),
        tradeLogApi.list({ ...base, executionMode: "PAPER" }),
      ]);
      return [...live.trades, ...paper.trades].filter(
        (t: TradeLogEntry) => t.horizon === "POSITIONAL" && t.exitReason === "OPEN",
      );
    },
    refetchInterval: POLL_MS,
  });
  if (q.isPending) {
    return <section className="card p-4 text-sm text-ink-secondary">Loading basket…</section>;
  }
  const baskets = q.data ?? [];
  return (
    <section className="card" data-testid="strategy-basket">
      <div className="mb-3 flex items-center gap-2">
        <i className="ti ti-basket text-lg text-accent" />
        <h3>Open basket</h3>
      </div>
      {baskets.length === 0 ? (
        <div className="text-sm text-ink-secondary">
          No open basket — the engine (or Take entry) places one when a signal fires.
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {baskets.map((b) => (
            <BasketTile key={b.id} basket={b} />
          ))}
        </div>
      )}
    </section>
  );
}
